import React, { Component } from 'react';
import styled from 'styled-components';
import { FiAlertTriangle, FiRefreshCw, FiHome } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Button from './Button';

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  padding: ${props => props.theme.spacing.xl};
`;

const ErrorCard = styled.div`
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing['4xl']};
  box-shadow: ${props => props.theme.shadows['2xl']};
  max-width: 600px;
  width: 100%;
  text-align: center;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing['2xl']};
  }
`;

const ErrorIcon = styled.div`
  font-size: 80px;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xl};
  animation: shake 0.5s ease-in-out;

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
    75% { transform: translateX(10px); }
  }
`;

const ErrorTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['3xl']};
  color: ${props => props.theme.colors.dark};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-weight: ${props => props.theme.fontWeights.bold};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.fontSizes['2xl']};
  }
`;

const ErrorMessage = styled.p`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.gray[600]};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  line-height: 1.6;
`;

const ErrorDetails = styled.details`
  margin-top: ${props => props.theme.spacing.xl};
  text-align: left;
  background: ${props => props.theme.colors.gray[100]};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};

  summary {
    cursor: pointer;
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.gray[700]};
    margin-bottom: ${props => props.theme.spacing.md};

    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }

  pre {
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.gray[700]};
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development mode
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // TODO: Log error to error reporting service (e.g., Sentry)
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <ErrorContainer>
          <ErrorCard>
            <ErrorIcon>
              <FiAlertTriangle />
            </ErrorIcon>

            <ErrorTitle>Oops! Something went wrong</ErrorTitle>

            <ErrorMessage>
              We're sorry for the inconvenience. An unexpected error occurred while loading this page. 
              Our team has been notified and is working on a fix.
            </ErrorMessage>

            <ButtonGroup>
              <Button
                variant="primary"
                size="large"
                onClick={this.handleReload}
              >
                <FiRefreshCw />
                Reload Page
              </Button>

              <Button
                as={Link}
                to="/"
                variant="secondary"
                size="large"
                onClick={this.handleReset}
              >
                <FiHome />
                Go to Homepage
              </Button>
            </ButtonGroup>

            {/* Show error details in development mode */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <ErrorDetails>
                <summary>Technical Details (Development Only)</summary>
                <pre>
                  <strong>Error:</strong> {this.state.error.toString()}
                  {'\n\n'}
                  <strong>Stack Trace:</strong>
                  {'\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </ErrorDetails>
            )}

            {this.state.errorCount > 2 && (
              <ErrorMessage style={{ marginTop: '1rem', color: '#dc2626' }}>
                <strong>Multiple errors detected.</strong> Please try clearing your browser cache or 
                contact support if the problem persists.
              </ErrorMessage>
            )}
          </ErrorCard>
        </ErrorContainer>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
